import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { assetService, type Asset, type AssetWithPermission } from '@/services/asset-service';

interface UseAssetLoaderOptions {
  requirePermission?: boolean;
}

export const useAssetLoader = (options: UseAssetLoaderOptions = {}) => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const [asset, setAsset] = useState<Asset | AssetWithPermission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; code?: number; assetName?: string } | null>(null);

  const { requirePermission = false } = options;

  useEffect(() => {
    const fetchAsset = async () => {
      if (assetId) {
        try {
          setLoading(true);
          setError(null);
          const fetchedAsset = await assetService.getAsset(assetId);

          if (requirePermission) {
            // Enhance with mock permission data (in real implementation, backend would return this)
            const assetWithPermission = {
              ...fetchedAsset,
              permission: {
                role: 'owner', // This would be determined by backend
                canRead: true,
                canUpdate: true,
                canDelete: true,
                canShare: true
              }
            };

            setAsset(assetWithPermission as AssetWithPermission);
          } else {
            setAsset(fetchedAsset);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Unknown error';

          // Check if it's a permission/access error
          // Backend can return "access denied", "no permission", "forbidden", "unauthorized", etc.
          const isAccessDenied = errorMessage.toLowerCase().includes('access denied') ||
                                  errorMessage.toLowerCase().includes('no permission') ||
                                  errorMessage.toLowerCase().includes('forbidden') ||
                                  errorMessage.toLowerCase().includes('unauthorized');

          // For public view (requirePermission=false), if asset is "not found", it might be access issue
          // Show request access page so user can request access instead of seeing "not found"
          const isPotentialAccessIssue = !requirePermission &&
                                          errorMessage.toLowerCase().includes('not found');

          if (isAccessDenied || isPotentialAccessIssue) {
            // Don't navigate, show request access page instead
            setError({
              message: errorMessage,
              code: isAccessDenied ? 403 : 404,
            });
          } else {
            // For other errors, show toast and navigate
            toast.error(`Failed to load asset: ${errorMessage}`);
            const navigatePath = requirePermission ? '/console/assets' : '/assets';
            navigate(navigatePath);
          }
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchAsset();
  }, [assetId, navigate, requirePermission]);

  return { asset, loading, assetId, error };
};